import React from 'react';
import { createPortal } from 'react-dom';
import classNames from 'classnames';
import { useGetProfileDataQuery } from '@/entities/Profile';
import { SITE_NAME } from '@/shared/config/projectConfig';
import { useScreenDetector } from '@/shared/lib/hooks/useScreenDetector';
import tournamentsUnavailableImage from '@/shared/assets/images/tournaments_unavailable.svg?url';
import LockIcon from '@/shared/assets/icons/lock.svg';
import LogoutIcon from '@/shared/assets/icons/logout.svg';
import InfoIcon from '@/shared/assets/icons/info-circle.svg';
import ProfileIcon from '@/shared/assets/icons/profile.svg';
import { Logo } from '@/shared/ui/Logo/Logo';
import { useLogout } from '@/features/Logout';
import headerStyle from '@/widgets/Header/ui/Header.module.scss';
import s from './BlockedPage.module.scss';

export const BlockedPage = () => {
  const { isMobile } = useScreenDetector();
  const { data: profile } = useGetProfileDataQuery();
  const { logout } = useLogout();

  const [dropdownOpen, setDropdownOpen] = React.useState(false);
  const [position, setPosition] = React.useState<{ top: number; left: number } | null>(null);

  const avatarRef = React.useRef<HTMLButtonElement>(null);
  const menuRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    document.title = `Учетная запись заблокирована | ${SITE_NAME}`;
  }, []);

  const handleLogout = () => {
    logout();
  };

  const calcPosition = React.useCallback(() => {
    const rect = avatarRef.current?.getBoundingClientRect();
    if (!rect) return null;

    const scrollX = window.scrollX ?? document.documentElement.scrollLeft;
    const scrollY = window.scrollY ?? document.documentElement.scrollTop;
    const menuWidth = 180;
    const rightMargin = 16;
    const left = Math.max(
      16,
      Math.min(rect.right + scrollX - menuWidth, scrollX + window.innerWidth - menuWidth - rightMargin),
    );

    return { top: rect.bottom + scrollY + 8, left };
  }, []);

  const closeDropdown = React.useCallback(() => setDropdownOpen(false), []);

  React.useEffect(() => {
    if (!dropdownOpen) {
      return undefined;
    }

    const handleClick = (event: MouseEvent) => {
      if (avatarRef.current?.contains(event.target as Node) || menuRef.current?.contains(event.target as Node)) {
        return;
      }
      closeDropdown();
    };

    const handleScroll = () => {
      const pos = calcPosition();
      if (pos) setPosition(pos);
    };

    document.addEventListener('mousedown', handleClick);
    window.addEventListener('resize', handleScroll);
    window.addEventListener('scroll', handleScroll, true);

    return () => {
      document.removeEventListener('mousedown', handleClick);
      window.removeEventListener('resize', handleScroll);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [dropdownOpen, closeDropdown, calcPosition]);

  const handleSupport = () => {
    window.location.href = 'mailto:info@tehnostrike.ru';
  };

  const isClub = profile?.accountType === 'club_account';
  const isAdmin = isClub && profile?.role === 'admin';
  const clubName = profile?.clubName || profile?.name || 'Клуб';
  const login = profile?.login || '';
  const nickname = profile?.nickname || '';

  const getDesktopGreeting = () => {
    if (isClub) {
      return isAdmin && login ? `${login} · ${clubName}` : clubName;
    }
    return nickname ? `Привет, ${nickname}!` : 'Привет!';
  };

  const getHeaderTextsMobile = () => {
    if (isClub) {
      if (isAdmin && login) {
        return {
          headerText: login,
          subText: clubName,
        };
      }
      return {
        headerText: clubName,
        subText: null,
      };
    }
    const playerGreeting = nickname ? `Привет, ${nickname}!` : 'Привет!';
    return {
      headerText: playerGreeting,
      subText: null,
    };
  };

  const desktopGreeting = getDesktopGreeting();
  const { headerText, subText } = getHeaderTextsMobile();
  const blockReason = profile?.blockReason || 'Нарушение условий использования сервиса';

  return (
    <div className={s.container}>
      {isMobile ? (
        <header className={headerStyle.HeaderMobile}>
          <div className={headerStyle.leftGroup}>
            <Logo size="xxs" className={headerStyle.logoMobile} />
            <div className={headerStyle.headerTextContainer}>
              <span className={headerStyle.greetingMobile}>{headerText}</span>
              {subText && <span className={headerStyle.subtitleMobile}>{subText}</span>}
            </div>
          </div>
          <div className={headerStyle.avatarWrapper}>
            <button
              ref={avatarRef}
              type="button"
              className={headerStyle.avatarCircle}
              onClick={(e) => {
                e.stopPropagation();
                const pos = calcPosition();
                if (pos) setPosition(pos);
                setDropdownOpen((prev) => !prev);
              }}
            >
              <ProfileIcon className={headerStyle.avatarIcon} />
            </button>
          </div>
        </header>
      ) : (
        <header className={s.header}>
          <Logo size="sm" className={s.logoDesktop} />
          <div className={s.greetingFrame}>
            <h2 className={s.greetingText}>{desktopGreeting}</h2>
            <div className={s.statusCircle}>
              <LockIcon className={s.statusIcon} />
            </div>
          </div>
          <button
            className={s.desktopLogoutBtn}
            onClick={handleLogout}
            title="Выйти из аккаунта"
            aria-label="Выйти из аккаунта"
            type="button"
          >
            <LogoutIcon className={s.logoutIcon} aria-hidden="true" />
          </button>
        </header>
      )}

      {dropdownOpen &&
        createPortal(
          <div className={headerStyle.menuPortal} style={{ top: position?.top ?? 0, left: position?.left ?? 0 }}>
            <div className={headerStyle.menu} role="menu" ref={menuRef}>
              <button
                type="button"
                className={classNames(headerStyle.menuItem, headerStyle.logoutItem)}
                onClick={() => {
                  handleLogout();
                  closeDropdown();
                }}
                role="menuitem"
              >
                Выйти
              </button>
            </div>
          </div>,
          document.body,
        )}

      <div className={s.contentWrapper}>
        <div className={s.reasonBadge}>
          <InfoIcon />
          <p className={s.reasonText}>{blockReason}</p>
        </div>

        <img src={tournamentsUnavailableImage} alt="Заблокировано" className={s.illustration} />

        <div className={s.textSection}>
          <h2 className={s.title}>Учетная запись заблокирована</h2>
          <p className={s.subtitle}>
            Доступ к личному кабинету ограничен. Для получения дополнительной информации обратитесь в службу поддержки.
          </p>
        </div>

        <button className={s.supportButton} onClick={handleSupport} type="button">
          Связаться с поддержкой
        </button>
      </div>
    </div>
  );
};
